import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import type { Template } from '@/api/v4/template.api';
import { fetchTemplateScanningStatus } from '@/api/v4/template.api';

export const useTemplateScanningStatus = (template: Template) => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();
  const [isError, setError] = useState(false);
  const { hasRequiredPermission } = useUserPermissions();

  const query = useQuery({
    queryKey: [QueryKey.TemplateScanningStatus, template.id],
    queryFn: () =>
      fetchTemplateScanningStatus({
        fileId: template.id,
      }),
    enabled:
      hasRequiredPermission(PERMISSION.MANAGE_ACCOUNT) &&
      template.scanningStatus === 'unscanned',
    staleTime: Infinity,
    refetchInterval: query => {
      const file = query.state.data;
      return !isError && file && file.scanningStatus === 'unscanned'
        ? 1000
        : false;
    },
  });

  const scanningStatus = query.data?.scanningStatus;

  useEffect(() => {
    if (!scanningStatus || scanningStatus === 'unscanned') return;

    void queryClient.invalidateQueries({ queryKey: [QueryKey.Templates] });

    if (scanningStatus === 'infected' || scanningStatus === 'error') {
      notify({
        message: t(
          scanningStatus === 'infected'
            ? 'actionPanel.files.infected'
            : 'actionPanel.files.scanningError',
          { name: template.name },
        ),
      });
    }
  }, [scanningStatus, queryClient, template.name, t]);

  useEffect(() => {
    if (query.isError) {
      setError(true);
      notify({ message: t`unknownError` });
    }
  }, [query.isError, t]);

  return query;
};
